import { useMemo, useState } from 'react';
import type { Account, Budget, ContributionCall, Fund, SaveBudget } from '@syndiqia/ledger/contracts';
import { errorMessage } from '../../api';
import { Alert, Empty, Loading, PageHeader } from '../../components/ui';
import { useI18n } from '../../i18n';
import { useAccounts, useBudget, useCalls, useCurrentYear, useFunds, useLedgerMutation } from '../../ledger-queries';
import { useSyndicate } from '../../session';
import { FinancesNav, toCents } from './FinancesNav';

export function BudgetPage() {
  const { t } = useI18n();
  const syndicate = useSyndicate();
  const { year, isPending } = useCurrentYear();
  const budget = useBudget(year?.id);
  const accounts = useAccounts();
  const funds = useFunds();
  const [editing, setEditing] = useState(false);

  if (!syndicate.can('ledger.read')) return <Alert tone="info">{t.common.forbidden}</Alert>;
  const ready = year && budget.data !== undefined && accounts.data && funds.data;

  return (
    <>
      <PageHeader title={t.finances.title} intro={year ? `${t.finances.year} ${year.label}` : undefined} />
      <FinancesNav />
      {(isPending || (year && !ready)) && <Loading label={t.loading} />}
      {!isPending && !year && <Empty>{t.finances.noYear}</Empty>}
      {ready &&
        (syndicate.can('ledger.budget.write') && (editing || budget.data === null) ? (
          <BudgetEditor
            yearId={year.id}
            budget={budget.data}
            accounts={accounts.data}
            funds={funds.data}
            onDone={() => setEditing(false)}
          />
        ) : (
          budget.data ? <BudgetView budget={budget.data} accounts={accounts.data} funds={funds.data} onEdit={() => setEditing(true)} /> : <Empty>{t.budget.noBudget}</Empty>
        ))}
    </>
  );
}

function useNames(accounts: Account[], funds: Fund[]) {
  const { locale } = useI18n();
  return {
    account: (n: string) => {
      const a = accounts.find((x) => x.number === n);
      return a ? `${a.number} — ${locale === 'fr' ? a.nameFr : a.nameEn}` : n;
    },
    fund: (code: string) => {
      const f = funds.find((x) => x.code === code);
      return f ? (locale === 'fr' ? f.nameFr : f.nameEn) : code;
    },
  };
}

function BudgetView({ budget, accounts, funds, onEdit }: { budget: Budget; accounts: Account[]; funds: Fund[]; onEdit: () => void }) {
  const { t, money, date } = useI18n();
  const { can } = useSyndicate();
  const names = useNames(accounts, funds);
  const calls = useCalls(budget.fiscalYearId);
  const adopt = useLedgerMutation<void, ContributionCall[]>((api, base) =>
    api(`${base}/fiscal-years/${budget.fiscalYearId}/budget/adopt`, { method: 'POST' }),
  );

  const roleOf = (n: string) => accounts.find((a) => a.number === n);
  const sections = [
    { title: t.budget.contributions, lines: budget.lines.filter((l) => roleOf(l.accountNumber)?.role === 'contribution') },
    {
      title: t.budget.otherRevenue,
      lines: budget.lines.filter((l) => roleOf(l.accountNumber)?.type === 'revenue' && roleOf(l.accountNumber)?.role !== 'contribution'),
    },
    { title: t.budget.expenses, lines: budget.lines.filter((l) => roleOf(l.accountNumber)?.type === 'expense') },
  ];
  const contributionTotal = sections[0]!.lines.reduce((s, l) => s + l.amountCents, 0);

  return (
    <>
      <div className="toolbar">
        <span className={`badge badge-${budget.status === 'adopted' ? 'active' : 'draft'}`}>{t.budget.status[budget.status]}</span>
        <span className="muted">
          {t.budget.installments} : {budget.installments}
        </span>
        {budget.status === 'draft' && (
          <>
            {can('ledger.budget.write') && <button type="button" className="button" onClick={onEdit}>
              {t.budget.edit}
            </button>}
            {can('ledger.budget.adopt') && <button
              type="button"
              className="button button-primary"
              disabled={adopt.isPending}
              onClick={() => window.confirm(t.budget.adoptConfirm) && adopt.mutate()}
            >
              {t.budget.adopt}
            </button>}
          </>
        )}
      </div>
      {adopt.isError && <Alert>{errorMessage(adopt.error, t)}</Alert>}

      {sections.map((section) =>
        section.lines.length === 0 ? null : (
          <section key={section.title}>
            <h2>{section.title}</h2>
            <table className="table">
              <thead>
                <tr>
                  <th>{t.budget.account}</th>
                  <th>{t.budget.fund}</th>
                  <th className="num">{t.budget.amount}</th>
                </tr>
              </thead>
              <tbody>
                {section.lines.map((l) => (
                  <tr key={`${l.accountNumber}-${l.fundCode}`}>
                    <td>{names.account(l.accountNumber)}</td>
                    <td>{names.fund(l.fundCode)}</td>
                    <td className="num">{money(l.amountCents)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan={2}>{t.budget.total}</th>
                  <th className="num">{money(section.lines.reduce((s, l) => s + l.amountCents, 0))}</th>
                </tr>
              </tfoot>
            </table>
          </section>
        ),
      )}
      <p className="muted">
        {t.budget.contributions} : {money(contributionTotal)} ({money(Math.round(contributionTotal / budget.installments))}{' '}
        {budget.installments === 12 ? t.budget.perMonth : `× ${budget.installments}`})
      </p>

      {budget.status === 'adopted' && (
        <section>
          <h2>{t.budget.calls}</h2>
          {calls.isPending && <Loading label={t.loading} />}
          <ul className="call-grid">
            {calls.data?.map((c) => (
              <li key={c.id} className={c.entryId ? 'call call-posted' : 'call'}>
                <span>{date(c.dueDate)}</span>
                <span className="muted">{c.entryId ? t.budget.posted : t.budget.scheduled}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

const BUDGET_FUND_KINDS = ['operating', 'contingency', 'self_insurance'];

function BudgetEditor({
  yearId,
  budget,
  accounts,
  funds,
  onDone,
}: {
  yearId: string;
  budget: Budget | null;
  accounts: Account[];
  funds: Fund[];
  onDone: () => void;
}) {
  const { t, money } = useI18n();
  const names = useNames(accounts, funds);
  const rows = useMemo(
    () =>
      accounts
        .filter((a) => a.type === 'revenue' || a.type === 'expense')
        .flatMap((a) =>
          funds
            .filter((f) => BUDGET_FUND_KINDS.includes(f.kind) && a.allowedFundKinds.includes(f.kind))
            .map((f) => ({ key: `${a.number}|${f.code}`, account: a, fund: f })),
        ),
    [accounts, funds],
  );
  const [installments, setInstallments] = useState(budget?.installments ?? 12);
  const [onlyNonZero, setOnlyNonZero] = useState(!!budget);
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries((budget?.lines ?? []).map((l) => [`${l.accountNumber}|${l.fundCode}`, (l.amountCents / 100).toFixed(2)])),
  );
  const save = useLedgerMutation<SaveBudget, Budget>((api, base, input) =>
    api(`${base}/fiscal-years/${yearId}/budget`, { method: 'PUT', body: input }),
  );

  const invalid = Object.entries(values).filter(([, v]) => v.trim() !== '' && toCents(v) === null).map(([k]) => k);
  const contributions = rows
    .filter((r) => r.account.role === 'contribution')
    .reduce((s, r) => s + (toCents(values[r.key] ?? '') ?? 0), 0);
  const visible = onlyNonZero ? rows.filter((r) => (toCents(values[r.key] ?? '') ?? 0) > 0) : rows;

  const submit = () => {
    const lines = rows
      .map((r) => ({ accountNumber: r.account.number, fundCode: r.fund.code, amountCents: toCents(values[r.key] ?? '') ?? 0 }))
      .filter((l) => l.amountCents > 0);
    save.mutate({ installments, lines }, { onSuccess: onDone });
  };

  return (
    <section className="panel">
      <div className="toolbar">
        <label className="inline-field">
          {t.budget.installments}
          <select value={installments} onChange={(e) => setInstallments(Number(e.target.value))}>
            {[12, 6, 4, 3, 2, 1].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <label className="inline-field">
          <input type="checkbox" checked={onlyNonZero} onChange={(e) => setOnlyNonZero(e.target.checked)} />
          {t.budget.onlyNonZero}
        </label>
      </div>
      <table className="table table-compact">
        <thead>
          <tr>
            <th>{t.budget.account}</th>
            <th>{t.budget.fund}</th>
            <th className="num">{t.budget.amount}</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((r) => (
            <tr key={r.key} className={r.account.role === 'contribution' ? 'row-highlight' : undefined}>
              <td>{names.account(r.account.number)}</td>
              <td>{names.fund(r.fund.code)}</td>
              <td className="num">
                <input
                  className={`input-num${invalid.includes(r.key) ? ' input-invalid' : ''}`}
                  value={values[r.key] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [r.key]: e.target.value }))}
                  inputMode="decimal"
                  aria-label={`${names.account(r.account.number)} — ${names.fund(r.fund.code)}`}
                />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={2}>{t.budget.contributions}</th>
            <th className="num">{money(contributions)}</th>
          </tr>
        </tfoot>
      </table>
      {save.isError && <Alert>{errorMessage(save.error, t)}</Alert>}
      <div className="form-actions">
        <button type="button" className="button button-primary" disabled={save.isPending || invalid.length > 0} onClick={submit}>
          {t.budget.save}
        </button>
        {budget && (
          <button type="button" className="button" onClick={onDone}>
            {t.common.cancel}
          </button>
        )}
      </div>
    </section>
  );
}
